import React from 'react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Activity, Moon, Sun, ArrowLeft, Shield, Lock, Eye,
  Database, UserCheck, Bell, FileText, Server, Globe,
  AlertTriangle, CheckCircle
} from 'lucide-react';

export default function PrivacyPage() {
  const { theme, toggleTheme } = useTheme();
  const effectiveDate = 'February 13, 2025';

  const sections = [
    {
      id: 'information-collected',
      title: '1. Information We Collect',
      icon: Database,
      content: (
        <>
          <p className="mb-4">We may collect:</p>
          
          <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Personal Information:</h4>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mb-4 space-y-1">
            <li>Name</li>
            <li>Date of birth</li>
            <li>Address</li>
            <li>Phone number</li>
            <li>Email address</li>
            <li>Government-issued identification (for verification)</li>
          </ul>

          <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Health Information:</h4>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mb-4 space-y-1">
            <li>Diagnoses</li>
            <li>Care needs</li>
            <li>Functional limitations</li>
            <li>Care preferences</li>
            <li>Uploaded medical documents</li>
          </ul>

          <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Clinician Information:</h4>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mb-4 space-y-1">
            <li>Professional license details</li>
            <li>Certifications</li>
            <li>Background check status</li>
            <li>Insurance information</li>
            <li>Experience and specialties</li>
          </ul>

          <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Technical Data:</h4>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-1">
            <li>Device information</li>
            <li>IP address</li>
            <li>Usage logs</li>
            <li>Encrypted session data</li>
          </ul>
        </>
      )
    },
    {
      id: 'how-we-use',
      title: '2. How We Use Your Information',
      icon: Eye,
      content: (
        <>
          <p className="mb-4">We use collected information to:</p>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
            <li>Assess care needs using AI analysis</li>
            <li>Generate match recommendations</li>
            <li>Verify clinician credentials</li>
            <li>Facilitate telehealth and in-person visits</li>
            <li>Process payments</li>
            <li>Improve our algorithms and services</li>
            <li>Comply with legal requirements</li>
          </ul>
        </>
      )
    },
    {
      id: 'hipaa',
      title: '3. HIPAA-Ready Architecture',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">CaraLoom is built to HIPAA standards. As we finalize Business Associate Agreements (BAAs) with our infrastructure and AI vendors, Protected Health Information (PHI) is handled so that it is:</p>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
            <li>Encrypted at rest and in transit</li>
            <li>Access-controlled using role-based permissions</li>
            <li>Logged and monitored for audit purposes</li>
            <li>Shared only when necessary to provide services</li>
          </ul>
        </>
      )
    },
    {
      id: 'background-checks',
      title: '4. Background Checks',
      icon: UserCheck,
      content: (
        <p className="text-slate-600 dark:text-slate-400">
          When in-person visits are selected, clinicians must complete identity verification and background screening through approved third-party services.
        </p>
      )
    },
    {
      id: 'data-sharing',
      title: '5. Data Sharing',
      icon: Globe,
      content: (
        <>
          <p className="font-semibold text-slate-900 dark:text-white mb-4">We do not sell personal information.</p>
          <p className="mb-4 text-slate-600 dark:text-slate-400">We may share data:</p>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
            <li>With matched clinicians (limited to necessary care details)</li>
            <li>With background check providers (when required)</li>
            <li>With payment processors (for billing)</li>
            <li>When required by law</li>
          </ul>
        </>
      )
    },
    {
      id: 'security',
      title: '6. Security',
      icon: Lock,
      content: (
        <>
          <p className="mb-4 text-slate-600 dark:text-slate-400">We implement:</p>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
            <li>End-to-end encryption (AES-256/TLS 1.2+)</li>
            <li>Multi-factor authentication</li>
            <li>Secure cloud hosting</li>
            <li>Continuous monitoring</li>
            <li>Restricted administrative access</li>
            <li>Periodic penetration testing</li>
            <li>Audit log tracking</li>
          </ul>
        </>
      )
    },
    {
      id: 'your-rights',
      title: '7. Your Rights',
      icon: FileText,
      content: (
        <>
          <p className="mb-4 text-slate-600 dark:text-slate-400">Users may:</p>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
            <li>Request access to their information</li>
            <li>Request corrections</li>
            <li>Request deletion (subject to legal obligations)</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p className="mt-4 text-slate-600 dark:text-slate-400">
            To submit a privacy request, contact: <a href="mailto:privacy@carematch.ai" className="text-cn-primary dark:text-blue-400 hover:underline">privacy@carematch.ai</a>
          </p>
        </>
      )
    },
    {
      id: 'ai-disclosure',
      title: '8. AI Use Disclosure',
      icon: Bell,
      content: (
        <>
          <p className="mb-4 text-slate-600 dark:text-slate-400">CaraLoom utilizes artificial intelligence to:</p>
          <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2 mb-4">
            <li>Assess patient care needs</li>
            <li>Generate match recommendations</li>
            <li>Assist in documentation summarization</li>
            <li>Identify potential care risks</li>
            <li>Improve matching accuracy</li>
          </ul>
          
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
            <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Important Notices:
            </h4>
            <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-1 text-sm">
              <li>AI recommendations are decision-support tools only</li>
              <li>Final clinical decisions are made exclusively by licensed clinicians</li>
              <li>AI output may contain errors and should be reviewed by users</li>
              <li>The platform continuously refines AI models based on anonymized performance data</li>
              <li>No automated decision-making will occur without an option for human review</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 'data-retention',
      title: '9. Record Retention Policy',
      icon: Server,
      content: (
        <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
          <li>Platform-generated documentation may be retained according to applicable federal and state laws</li>
          <li>Users may request record copies subject to identity verification</li>
          <li>Data retention timelines vary by state and service type</li>
          <li>Upon termination of an account, data may be archived as required by law</li>
        </ul>
      )
    },
    {
      id: 'breach-notification',
      title: '10. Data Breach Notification',
      icon: AlertTriangle,
      content: (
        <p className="text-slate-600 dark:text-slate-400">
          If a data breach occurs, affected users will be notified in accordance with applicable law. CaraLoom maintains continuous monitoring for breaches and has incident response protocols in place.
        </p>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-cn-bone-100 via-white to-cn-bone-200 dark:from-cn-ink-950 dark:via-cn-ink-900 dark:to-cn-ink-950" data-testid="privacy-page">
      <Seo
        title="Privacy Policy | CaraLoom"
        description="How CaraLoom collects, uses, and protects your personal and health information — HIPAA-ready architecture, encryption, and your data rights."
        path="/privacy"
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Privacy', path: '/privacy' }])]}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="pt-12 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-cn-primary dark:text-blue-400 hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-cn-primary dark:text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white font-display mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-2">
              Effective Date: {effectiveDate}
            </p>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              CaraLoom ("Company," "we," "us," or "our") is committed to protecting the privacy and security of your personal and health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {sections.map((section) => (
            <Card key={section.id} id={section.id} className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                    {section.title}
                  </h2>
                </div>
                <div className="pl-16">
                  {section.content}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Marketplace Liability & User Diligence */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display mb-4">
                Marketplace Liability & Your Responsibility
              </h2>
              <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                <p>
                  <span className="font-semibold text-slate-900 dark:text-white">CaraLoom is a technology marketplace, not a healthcare provider or staffing agency.</span> We do not employ clinicians and are not responsible for the conduct of any user of our platform. Clinicians are independent licensed professionals running their own practices.
                </p>
                <p>
                  <span className="font-semibold text-slate-900 dark:text-white">Background checks are not always 100% accurate</span>, may not capture an individual's entire criminal or licensure history, and cannot guarantee safety. Verification badges reflect checks performed at clinician onboarding and may not reflect later events. You must do your own diligence before booking.
                </p>
                <p>
                  All information in clinician profiles, reviews, and messages is user-generated and not independently verified by CaraLoom. <span className="font-semibold text-slate-900 dark:text-white">Cara and AI features are informational only</span> — not a substitute for professional medical advice. For emergencies, call 911.
                </p>
                <p>
                  CaraLoom℠ and the woven-knot mark are service marks of CaraLoom, Inc. © {new Date().getFullYear()} CaraLoom, Inc. All rights reserved.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-gradient-to-r from-cn-primary to-cn-sage-500 dark:from-blue-900/20 dark:to-indigo-900/20 border-0">
            <CardContent className="p-6 lg:p-8 text-center">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display mb-4">
                Questions About Our Privacy Policy?
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                If you have any questions or concerns about this Privacy Policy, please contact us.
              </p>
              <a href="mailto:privacy@caraloom.com" className="text-cn-primary dark:text-blue-400 font-medium hover:underline">
                privacy@caraloom.com
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-900">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500 dark:text-slate-400">
          <p>© {new Date().getFullYear()} CaraLoom. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
